import { useForm } from "react-hook-form";

import OTPInput from "components/Auth/OTPInput";
import { BUTTON_BOX_CLASS, CONTINUE_BUTTON_CLASS } from "components/FormV2";
import AuthLayout from "layouts/AuthLayout";
import type { ConfirmSurface } from "pages/StartLead/events";

import { SupportCallLine } from "./GrantParts";
import { SUPPORT_LINES, type SupportLineKind } from "./login-grant-flow";
import { DestinationLine, DestinationSlot } from "./VerifyScreen";

/** Stands in for the phone number while the grant is unknown. It is the
 * width a masked or typed number sets, so the line wraps as the loaded
 * screen's does. */
const PLACEHOLDER_PHONE = "(555) 123-4567";

const BAR_CLASS = "animate-pulse rounded bg-oat-200 motion-reduce:animate-none";

function Bar({ className }: { className: string }) {
  return (
    <span aria-hidden className={`inline-block ${BAR_CLASS} ${className}`} />
  );
}

/** The text layout's shape before the grant is looked up: the same chrome, and
 * a placeholder in each slot the loaded screen fills, so nothing below the
 * title moves when it arrives. Only the call line is real. */
export default function GrantScreenSkeleton({
  supportKind,
  surface,
  withFix,
}: {
  supportKind: SupportLineKind;
  surface: ConfirmSurface;
  /** The host can save a correction, so the loaded screen may carry the fix
   * row; a host that cannot never does. */
  withFix: boolean;
}) {
  const { control } = useForm({ defaultValues: { code: "" } });
  return (
    <AuthLayout
      title="Enter your code"
      description=""
      variant="quote_flow"
      titlePlaceholder
      headerPhone={SUPPORT_LINES[supportKind]}
      bodySpacing="screen"
      subheader={
        <DestinationSlot>
          <DestinationLine
            method="sms_otp"
            phase={null}
            destination={PLACEHOLDER_PHONE}
            overlay={
              <p className="col-start-1 row-start-1">
                <Bar className="h-[0.9em] w-72 max-w-full align-middle" />
              </p>
            }
          />
          {withFix && (
            <div data-slot="fix" className="mt-1 flex min-h-5 items-center">
              <Bar className="h-3 w-24" />
            </div>
          )}
        </DestinationSlot>
      }
    >
      <div aria-busy className="space-y-5">
        <div>
          <div data-slot="code">
            <OTPInput
              name="code"
              control={control}
              length={6}
              disabled
              busy
              align="start"
            />
          </div>
          <p data-slot="code-message" className="mt-2 min-h-5" />
          <button
            type="button"
            disabled
            className={`${CONTINUE_BUTTON_CLASS} mt-2 w-full`}
          >
            Continue
          </button>
        </div>
        <div className="flex min-h-11 items-center">
          <Bar className="h-4 w-28" />
        </div>
        <div data-slot="email">
          <div
            aria-hidden
            className={`${BUTTON_BOX_CLASS} w-full border-oat-200 bg-oat-100 ${BAR_CLASS}`}
          >
            &nbsp;
          </div>
        </div>
        <SupportCallLine kind={supportKind} surface={surface} screen="code" />
      </div>
    </AuthLayout>
  );
}
